import { LoadingIndicator } from 'components'
import ErrorBoundary from 'components/ErrorBoundary'
import ProtectedRouteWithDashboard from 'components/ProtectedRouteWithDashboard'
import { UserRoleEnum } from 'enums'
import { useToken } from 'hooks'
import Home from 'pages/Home'
import { ViewCategories } from 'pages/admin/categories'
import { Dashboard, DashboardUser } from 'pages/admin/dashboard'
import { AddInvoice, EditInvoice, ViewInvoice, ViewInvoices } from 'pages/admin/invoices'
import { AddPartner, EditPartner, ViewPartner, ViewPartners } from 'pages/admin/partners'
import { AddProduct, EditProduct, ViewProduct, ViewProducts } from 'pages/admin/products'
import { Settings } from 'pages/admin/settings'
import { AddSubscription, EditSubscription, ViewSubscription, ViewSubscriptions } from 'pages/admin/subscriptions'
import { ViewSystems } from 'pages/admin/systems'
import { AddTenant, EditTenant, ViewTenant, ViewTenants } from 'pages/admin/tenants'
import { AddUser, EditUser, ViewUser, ViewUsers } from 'pages/admin/users'
import { ForgotPassword, Login, ResetPassword } from 'pages/auth'
import { Marketplace, MarketplaceDetails } from 'pages/marketplace'
import { Invoices } from 'pages/tenant/invoices'
import { Products } from 'pages/tenant/products'
import { TenantSettings } from 'pages/tenant/settings'
import { Subscriptions, ViewTenantSubscription } from 'pages/tenant/subscriptions'
import { Users, ViewTenantUser } from 'pages/tenant/users'
import React, { Suspense, useEffect } from 'react'
import { Navigate, Route, Routes, useLocation, useNavigate } from 'react-router'
import { Page404 } from './pages'

const App: React.FC = () => {
  const location = useLocation()
  const navigate = useNavigate()
  const { token, setToken, loading } = useToken()

  useEffect(() => {
    window.scrollTo(0, 0)
    return () => {}
  }, [location])

  useEffect(() => {
    if (!token && !['/login', '/set-password', '/forgot-password'].includes(location.pathname)) {
      navigate(`/login?redirectTo=${location.pathname}`)
    }
    return () => {}
  }, [token, location.pathname, navigate])

  if (loading) {
    return <LoadingIndicator />
  }

  return (
    <Suspense fallback={<LoadingIndicator />}>
      <ErrorBoundary>
        <div className='bg-slate-100 h-screen'>
          <Routes>
            <Route path='/' element={<Home />} />
            <Route path='login' element={<Login setToken={setToken} />} />
            <Route path='set-password' element={<ResetPassword />} />
            <Route path='forgot-password' element={<ForgotPassword />} />

            <Route path='admin'>
              <Route
                index
                element={
                  <ProtectedRouteWithDashboard role={UserRoleEnum.ADMIN}>
                    <Navigate to='/admin/dashboard' />
                  </ProtectedRouteWithDashboard>
                }
              />
              <Route
                path='dashboard'
                element={
                  <ProtectedRouteWithDashboard role={UserRoleEnum.ADMIN}>
                    <Dashboard />
                  </ProtectedRouteWithDashboard>
                }
              />

              <Route path='subscriptions'>
                <Route
                  index
                  element={
                    <ProtectedRouteWithDashboard role={UserRoleEnum.ADMIN}>
                      <ViewSubscriptions />
                    </ProtectedRouteWithDashboard>
                  }
                />
                <Route
                  path='new'
                  element={
                    <ProtectedRouteWithDashboard role={UserRoleEnum.ADMIN}>
                      <AddSubscription />
                    </ProtectedRouteWithDashboard>
                  }
                />
                <Route
                  path='new/:tenantId'
                  element={
                    <ProtectedRouteWithDashboard role={UserRoleEnum.ADMIN}>
                      <AddSubscription />
                    </ProtectedRouteWithDashboard>
                  }
                />
                <Route
                  path='edit/:subscriptionId'
                  element={
                    <ProtectedRouteWithDashboard role={UserRoleEnum.ADMIN}>
                      <EditSubscription />
                    </ProtectedRouteWithDashboard>
                  }
                />
                <Route
                  path=':subscriptionId'
                  element={
                    <ProtectedRouteWithDashboard role={UserRoleEnum.ADMIN}>
                      <ViewSubscription />
                    </ProtectedRouteWithDashboard>
                  }
                />
              </Route>

              <Route path='tenants'>
                <Route
                  index
                  element={
                    <ProtectedRouteWithDashboard role={UserRoleEnum.ADMIN}>
                      <ViewTenants />
                    </ProtectedRouteWithDashboard>
                  }
                />
                <Route
                  path='new'
                  element={
                    <ProtectedRouteWithDashboard role={UserRoleEnum.ADMIN}>
                      <AddTenant />
                    </ProtectedRouteWithDashboard>
                  }
                />
                <Route
                  path=':tenantId'
                  element={
                    <ProtectedRouteWithDashboard role={UserRoleEnum.ADMIN}>
                      <ViewTenant />
                    </ProtectedRouteWithDashboard>
                  }
                />
                <Route
                  path=':tenantId'
                  element={
                    <ProtectedRouteWithDashboard role={UserRoleEnum.ADMIN}>
                      <ViewTenant />
                    </ProtectedRouteWithDashboard>
                  }
                />
                <Route
                  path='edit/:tenantId'
                  element={
                    <ProtectedRouteWithDashboard role={UserRoleEnum.ADMIN}>
                      <EditTenant />
                    </ProtectedRouteWithDashboard>
                  }
                />
              </Route>

              <Route path='partners'>
                <Route
                  index
                  element={
                    <ProtectedRouteWithDashboard role={UserRoleEnum.ADMIN}>
                      <ViewPartners />
                    </ProtectedRouteWithDashboard>
                  }
                />
                <Route
                  path='new'
                  element={
                    <ProtectedRouteWithDashboard role={UserRoleEnum.ADMIN}>
                      <AddPartner />
                    </ProtectedRouteWithDashboard>
                  }
                />
                <Route
                  path='edit/:partnerId'
                  element={
                    <ProtectedRouteWithDashboard role={UserRoleEnum.ADMIN}>
                      <EditPartner />
                    </ProtectedRouteWithDashboard>
                  }
                />
                <Route
                  path=':partnerId'
                  element={
                    <ProtectedRouteWithDashboard role={UserRoleEnum.ADMIN}>
                      <ViewPartner />
                    </ProtectedRouteWithDashboard>
                  }
                />
              </Route>

              <Route path='users'>
                <Route
                  index
                  element={
                    <ProtectedRouteWithDashboard role={UserRoleEnum.ADMIN}>
                      <ViewUsers />
                    </ProtectedRouteWithDashboard>
                  }
                />
                <Route
                  path='new'
                  element={
                    <ProtectedRouteWithDashboard role={UserRoleEnum.ADMIN}>
                      <AddUser />
                    </ProtectedRouteWithDashboard>
                  }
                ></Route>
                <Route
                  path=':userId'
                  element={
                    <ProtectedRouteWithDashboard role={UserRoleEnum.ADMIN}>
                      <ViewUser />
                    </ProtectedRouteWithDashboard>
                  }
                ></Route>
                <Route
                  path='edit/:userId'
                  element={
                    <ProtectedRouteWithDashboard role={UserRoleEnum.ADMIN}>
                      <EditUser />
                    </ProtectedRouteWithDashboard>
                  }
                ></Route>
              </Route>

              <Route path='settings'>
                <Route
                  index
                  element={
                    <ProtectedRouteWithDashboard role={UserRoleEnum.ADMIN}>
                      <Settings />
                    </ProtectedRouteWithDashboard>
                  }
                />
              </Route>

              <Route path='invoices'>
                <Route
                  index
                  element={
                    <ProtectedRouteWithDashboard role={UserRoleEnum.ADMIN}>
                      <ViewInvoices />
                    </ProtectedRouteWithDashboard>
                  }
                />

                <Route
                  path='new'
                  element={
                    <ProtectedRouteWithDashboard role={UserRoleEnum.ADMIN}>
                      <AddInvoice />
                    </ProtectedRouteWithDashboard>
                  }
                />

                <Route
                  path='edit/:invoiceId'
                  element={
                    <ProtectedRouteWithDashboard role={UserRoleEnum.ADMIN}>
                      <EditInvoice />
                    </ProtectedRouteWithDashboard>
                  }
                />

                <Route
                  path='view/:invoiceId'
                  element={
                    <ProtectedRouteWithDashboard role={UserRoleEnum.ADMIN}>
                      <ViewInvoice />
                    </ProtectedRouteWithDashboard>
                  }
                />
              </Route>
              <Route path='products'>
                <Route
                  index
                  element={
                    <ProtectedRouteWithDashboard role={UserRoleEnum.ADMIN}>
                      <ViewProducts />
                    </ProtectedRouteWithDashboard>
                  }
                />
                <Route
                  path='new'
                  element={
                    <ProtectedRouteWithDashboard role={UserRoleEnum.ADMIN}>
                      <AddProduct />
                    </ProtectedRouteWithDashboard>
                  }
                />
                <Route
                  path=':scopeId'
                  element={
                    <ProtectedRouteWithDashboard role={UserRoleEnum.ADMIN}>
                      <ViewProduct />
                    </ProtectedRouteWithDashboard>
                  }
                />
                <Route
                  path=':scopeId/edit'
                  element={
                    <ProtectedRouteWithDashboard role={UserRoleEnum.ADMIN}>
                      <EditProduct />
                    </ProtectedRouteWithDashboard>
                  }
                />
              </Route>

              <Route path='categories'>
                <Route
                  index
                  element={
                    <ProtectedRouteWithDashboard role={UserRoleEnum.ADMIN}>
                      <ViewCategories />
                    </ProtectedRouteWithDashboard>
                  }
                />
              </Route>

              <Route path='systems'>
                <Route
                  index
                  element={
                    <ProtectedRouteWithDashboard role={UserRoleEnum.ADMIN}>
                      <ViewSystems />
                    </ProtectedRouteWithDashboard>
                  }
                />
              </Route>
            </Route>

            <Route path='tenant'>
              <Route
                index
                element={
                  <ProtectedRouteWithDashboard role={[UserRoleEnum.TENANT_ADMIN, UserRoleEnum.TENANT_USER]}>
                    <Navigate to='/tenant/dashboard' />
                  </ProtectedRouteWithDashboard>
                }
              />
              <Route
                path='dashboard'
                element={
                  <ProtectedRouteWithDashboard role={[UserRoleEnum.TENANT_ADMIN, UserRoleEnum.TENANT_USER]}>
                    <DashboardUser />
                  </ProtectedRouteWithDashboard>
                }
              />
              <Route
                path='subscriptions'
                element={
                  <ProtectedRouteWithDashboard role={[UserRoleEnum.TENANT_ADMIN, UserRoleEnum.TENANT_USER]}>
                    <Subscriptions />
                  </ProtectedRouteWithDashboard>
                }
              />
              <Route
                path='subscriptions/:subscriptionId'
                element={
                  <ProtectedRouteWithDashboard role={[UserRoleEnum.TENANT_ADMIN, UserRoleEnum.TENANT_USER]}>
                    <ViewTenantSubscription />
                  </ProtectedRouteWithDashboard>
                }
              />
              <Route
                path='settings'
                element={
                  <ProtectedRouteWithDashboard role={[UserRoleEnum.TENANT_ADMIN, UserRoleEnum.TENANT_USER]}>
                    <TenantSettings />
                  </ProtectedRouteWithDashboard>
                }
              />
              <Route
                path='invoices'
                element={
                  <ProtectedRouteWithDashboard role={[UserRoleEnum.TENANT_ADMIN, UserRoleEnum.TENANT_USER]}>
                    <Invoices />
                  </ProtectedRouteWithDashboard>
                }
              ></Route>
              <Route
                path='products'
                element={
                  <ProtectedRouteWithDashboard role={[UserRoleEnum.TENANT_ADMIN, UserRoleEnum.TENANT_USER]}>
                    <Products />
                  </ProtectedRouteWithDashboard>
                }
              />
              <Route
                path='users'
                element={
                  <ProtectedRouteWithDashboard role={[UserRoleEnum.TENANT_ADMIN, UserRoleEnum.TENANT_USER]}>
                    <Users />
                  </ProtectedRouteWithDashboard>
                }
              />

              <Route
                path='users/:userId'
                element={
                  <ProtectedRouteWithDashboard role={[UserRoleEnum.TENANT_ADMIN, UserRoleEnum.TENANT_USER]}>
                    <ViewTenantUser />
                  </ProtectedRouteWithDashboard>
                }
              />
            </Route>

            <Route path='marketplace'>
              <Route
                index
                element={
                  <ProtectedRouteWithDashboard
                    role={[UserRoleEnum.ADMIN, UserRoleEnum.TENANT_ADMIN, UserRoleEnum.TENANT_USER]}
                  >
                    <Marketplace />
                  </ProtectedRouteWithDashboard>
                }
              />
              <Route
                path='system/:systemId'
                element={
                  <ProtectedRouteWithDashboard
                    role={[UserRoleEnum.ADMIN, UserRoleEnum.TENANT_ADMIN, UserRoleEnum.TENANT_USER]}
                  >
                    <MarketplaceDetails />
                  </ProtectedRouteWithDashboard>
                }
              />
            </Route>

            <Route path='*' element={<Page404 />}></Route>
          </Routes>
        </div>
      </ErrorBoundary>
    </Suspense>
  )
}

export default App
